<script>
    import { goto } from '$app/navigation';
    import { page } from '$app/stores';

    import Button from './Button.svelte';
    import Card from './Card.svelte';
    import H2 from './H2.svelte';
    import H3 from './H3.svelte';
    import Modal from './Modal.svelte';
    import ProductIcon from './ProductIcon.svelte';
    import { img_cdn, img_cdn_srcset } from './utils/image';
    import { worksheets_nav_items } from '../routes/worksheets/worksheets_common.js';
    import { resources_nav_items } from '../routes/resources/resources_common.js';
    import { courses_nav_items } from '../routes/courses/courses_common.js';
    import { logEvent } from './utils/logs';

    export let content = {};
    export let shown = false;
    let selected_interest = '';
    const interest_options = content.interest_options || [];
    const featured_bundle = content.featured_bundle || null;
    const bundles = $page.data.bundles || [];
    const image_interest_options = interest_options.filter(
        (option) => option.image
    );
    const both_option =
        interest_options.find((option) => option.value === 'resources') || null;
    const featured_bundle_data = featured_bundle
        ? bundles.find(
              (bundle) => bundle.text_id === featured_bundle.text_id
          ) || null
        : null;
    const featured_bundle_items = featured_bundle_data?.items || [];
    const featured_bundle_href = featured_bundle_data
        ? `/bundles/${featured_bundle_data.slug}`
        : '';

    const close = () => {
        shown = false;
        selected_interest = '';
    };

    const handle_interest_select = (interest) => {
        selected_interest = interest;
    };

    const go_back_to_interest = () => {
        selected_interest = '';
    };

    const get_selected_interest_option = () =>
        interest_options.find((option) => option.value === selected_interest) ||
        null;

    const PATH_CONFIGS = {
        courses: {
            base_path: '/courses',
            nav_items: courses_nav_items
        },
        worksheets: {
            base_path: '/worksheets',
            nav_items: worksheets_nav_items
        },
        resources: {
            base_path: '/resources',
            nav_items: resources_nav_items
        }
    };

    const get_path_config = (interest) =>
        PATH_CONFIGS[interest] || PATH_CONFIGS.worksheets;

    const get_age_groups_for_selection = (interest = '') => {
        const config = get_path_config(interest);

        return config.nav_items
            .filter((item) => item.path)
            .map((item) => ({
                ...item,
                href: `${config.base_path}/${item.path}`
            }));
    };

    const filter_items_by_age = (items = [], age_group) => {
        const selected_min = age_group?.min_age ?? 0;
        const selected_max = age_group?.max_age ?? 100;

        return items.filter((item) => {
            const item_min = item.min_age ?? 0;
            const item_max = item.max_age ?? 100;

            return item_max >= selected_min && item_min <= selected_max;
        });
    };

    const get_item_key = (item = {}) =>
        `${item.type || 'product'}:${
            item.slug || item.id || item.text_id || ''
        }`;

    const dedupe_items = (items = []) => {
        const seen = new Set();

        return items.filter((item) => {
            const key = get_item_key(item);

            if (!key || seen.has(key)) {
                return false;
            }

            seen.add(key);
            return true;
        });
    };

    const get_items_for_interest = (interest = '') => {
        const page_data = $page.data;

        if (interest === 'courses') {
            return dedupe_items(
                (page_data.courses || []).filter(
                    (course) => course.price && course.listed !== false
                )
            );
        }

        if (interest === 'worksheets') {
            return dedupe_items(
                (page_data.worksheets || []).filter(
                    (worksheet) => worksheet.listed !== false
                )
            );
        }

        return [];
    };

    const get_product_href = (item = {}) => {
        if (!item.slug) return '';

        if (item.type === 'course') return `/courses/${item.slug}`;
        if (item.type === 'worksheet') return `/worksheets/${item.slug}`;

        return '';
    };

    const get_preview_items = (interest, age_group) => {
        if (interest === 'resources') {
            const matched_courses = filter_items_by_age(
                get_items_for_interest('courses'),
                age_group
            ).slice(0, 2);
            const matched_worksheets = filter_items_by_age(
                get_items_for_interest('worksheets'),
                age_group
            ).slice(0, 2);

            return dedupe_items([
                ...matched_courses,
                ...matched_worksheets
            ]).slice(0, 4);
        }

        return filter_items_by_age(
            get_items_for_interest(interest),
            age_group
        ).slice(0, 4);
    };

    const get_age_groups_with_previews = (interest = '') =>
        get_age_groups_for_selection(interest).map((age_group) => {
            const preview_items = get_preview_items(interest, age_group);
            const href =
                preview_items.length === 1
                    ? get_product_href(preview_items[0])
                    : age_group.href;

            return {
                ...age_group,
                href,
                preview_items
            };
        });

    const handle_age_group_select = async (age_group) => {
        logEvent('hero-explore-select', {
            interest: selected_interest,
            age_group: age_group.path || 'all'
        });
        close();
        await goto(age_group.href);
    };

    const handle_featured_bundle_select = async () => {
        if (!featured_bundle_href) return;
        close();
        logEvent('hero-explore-select-bundle');
        await goto(featured_bundle_href);
    };
</script>

{#if shown}
    <Modal on:close={close}>
        <svelte:fragment slot="header">
            <H2 class="text-center my-4" invert>
                {#if !selected_interest}
                    {@html content.title}
                {:else}
                    {@html get_selected_interest_option()?.header ||
                        content.title}
                {/if}
            </H2>
        </svelte:fragment>

        {#if !selected_interest}
            <div class="space-y-4">
                <div class="flex flex-row flex-wrap sm:flex-nowrap gap-4">
                    {#each image_interest_options as option (option.value)}
                        <Card
                            invert
                            clickable
                            class="basis-1/2 flex-grow min-w-64 min-h-72 flex flex-col gap-2 items-center justify-center text-center"
                            on:click={() =>
                                handle_interest_select(option.value)}
                        >
                            <img
                                alt=""
                                src={img_cdn(option.image, {
                                    w: 375,
                                    h: 163,
                                    fit: 'contain'
                                })}
                                srcset={img_cdn_srcset(option.image, {
                                    w: 375,
                                    h: 163,
                                    fit: 'contain'
                                })}
                                class="mx-auto w-full aspect-[375/163]"
                                loading="lazy"
                            />
                            <H3 class="text-center" invert
                                >{@html option.title}</H3
                            >
                            <p class="text-balance text-center">
                                {@html option.description}
                            </p>
                        </Card>
                    {/each}
                </div>
                {#if both_option}
                    <Card
                        invert
                        clickable
                        class="text-center"
                        on:click={() =>
                            handle_interest_select(both_option.value)}
                    >
                        <H3 invert>{@html both_option.title}</H3>
                    </Card>
                {/if}
                {#if featured_bundle}
                    <Card
                        invert
                        clickable
                        class="w-full !mt-8"
                        on:click={handle_featured_bundle_select}
                    >
                        <div
                            class="flex flex-col items-center gap-4 text-center"
                        >
                            <H3 invert class="pt-4"
                                >{@html featured_bundle.title}</H3
                            >
                            <p class="text-balance">
                                {@html featured_bundle.description}
                            </p>
                            {#if featured_bundle_items.length}
                                <div
                                    class="flex w-9/12 flex-nowrap justify-around py-8"
                                >
                                    {#each featured_bundle_items as item, index}
                                        <ProductIcon
                                            product={item}
                                            mark_purchased={false}
                                            class="-ml-16 first:ml-0 shadow-lg"
                                            style="z-index: {featured_bundle_items.length -
                                                index};"
                                        />
                                    {/each}
                                </div>
                            {/if}
                        </div>
                    </Card>
                {/if}
            </div>
        {:else}
            <div class="space-y-4">
                <div class="grid gap-3">
                    {#each get_age_groups_with_previews(selected_interest) as age_group (age_group.path)}
                        <Card
                            invert
                            clickable
                            class="flex justify-between items-center"
                            on:click={() => handle_age_group_select(age_group)}
                        >
                            <H3 invert>{age_group.title}</H3>
                            {#if age_group.preview_items.length}
                                <div class="flex flex-nowrap items-center">
                                    {#each age_group.preview_items as item, index (get_item_key(item))}
                                        <ProductIcon
                                            product={item}
                                            mark_purchased={false}
                                            class="-ml-8 first:ml-0 !w-12"
                                            style="z-index: {age_group
                                                .preview_items.length - index};"
                                        />
                                    {/each}
                                </div>
                            {/if}
                        </Card>
                    {/each}
                </div>

                <div class="flex gap-4 justify-between">
                    <Button outline invert on:click={go_back_to_interest}
                        >{content.back_cta || 'Back'}</Button
                    >

                    {#if selected_interest !== 'worksheets'}
                        <Button lg primary invert route="/for-schools"
                            >{content.classrooms_cta ||
                                'For Classrooms'}</Button
                        >
                    {/if}
                </div>
            </div>
        {/if}
    </Modal>
{/if}
